import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { MemberStatementCoverage } from './useStatementCoverage.interface';
import fetchStatementCoverage from './useStatementCoverage.request';

import { useMember } from 'modules/common/queries/member';

export const STATEMENT_COVERAGE_QUERY_KEY = ['bookkeeping', 'useStatementCoverage'];

export * from './useStatementCoverage.interface';

/**
 * Whether a coverage payload can be reasoned about — one definition, so the section and the
 * Continue gate cannot disagree from the same cache.
 *
 * Keyed on the payload, never the query's error flag: React Query keeps the last good `data`
 * when a refetch fails, and treating that as "no coverage" would discard it.
 *
 * `accounts_unavailable` means "we could not look", so an empty `accounts` beside it is never
 * "this member has no accounts".
 *
 * A null `coverage_start` is deliberately *not* checked here. The accounts loaded fine; only
 * the range is unknown, and folding that in would report a load failure to a member who simply
 * has no S-corp election date yet. Callers handle that state on its own terms.
 */
/** What to tell the member when coverage answered but cannot report accounts. Beside the predicate that decides it. */
export const COVERAGE_UNAVAILABLE_MESSAGE =
    "We couldn't load your bank accounts just now. Refresh to try again — your uploads are safe.";

export function isCoverageUsable(coverage: MemberStatementCoverage | undefined): coverage is MemberStatementCoverage {
    return coverage != null && !coverage.accounts_unavailable;
}

/**
 * Per-account statement coverage: which months we require and which we already hold. The
 * source of truth for both the ask and the progress bar.
 *
 * Not polled — a read costs a Tight + Plaid round trip. Callers invalidate instead, on the
 * transitions that change the answer: a delete, a modal close after an upload, and a verdict
 * reaching `matched` before coverage credits it.
 */
export default function useStatementCoverage(
    options?: Omit<UseQueryOptions<MemberStatementCoverage, AxiosError>, 'queryKey' | 'queryFn'>
) {
    const { data: member } = useMember();
    const memberId = member?.id;
    // Pulled out so a caller's own `enabled` cannot clobber the member guard below.
    const { enabled: isEnabledByCaller = true, ...restOptions } = options ?? {};

    return useQuery<MemberStatementCoverage, AxiosError>({
        queryKey: [...STATEMENT_COVERAGE_QUERY_KEY, memberId ?? null],
        queryFn: () => fetchStatementCoverage(memberId as number),
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        retry: false,
        ...restOptions,
        // The endpoint requires member_id; firing before useMember resolves would 400.
        enabled: isEnabledByCaller && memberId != null,
    });
}
